import { useState, useEffect, useMemo } from 'react'
import { Button, TextField } from '../components/ui'
import { Select } from '../components/ui/Select'
import { useI18n } from '../i18n'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { GovernanceListingSection } from '../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableLoadingRow } from '../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import adminStyles from '@/styles/pages/AdminPanel.module.scss'
import { UserPlus, Edit, Trash2, XCircle, AlertTriangle, KeyRound, Users } from 'lucide-react'
import PermissionsManagement from '../components/admin/PermissionsManagement'
import UserStandardAccessPanel from '../components/admin/UserStandardAccessPanel'
import { AdminNavTabs } from '../components/admin/AdminNavTabs'
import { PageLayout } from '../layouts/PageLayout'

type OrgRecord = {
  id: string
  name: string
  userCount?: number
  assetCount?: number
  deletionStatus?: 'ACTIVE' | 'PENDING_DELETION'
  deletionScheduledFor?: string | null
  deletionComment?: string | null
}

type UserOrganization = string | { id: string; name: string }

interface User {
  id: string
  username: string
  email: string
  role: string
  permissions: string[]
  createdAt: string
  lastLogin: string
  status?: string
  fullName?: string | null
  phone?: string | null
  position?: string | null
  organizations?: UserOrganization[]
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  interface UserFormData {
    username: string
    email: string
    password: string
    role: string
    permissions: string[]
    organization: string
    organizations: string[]
    fullName: string
    phone: string
    position: string
  }


// Enhanced permissions with categories and detailed descriptions for PermissionsManagement component
const availablePermissionsWithCategories = [
  { 
    id: 'access.dashboard', 
    name: 'Dashboard', 
    category: 'access', 
    description: 'Доступ к главной панели управления (Home). Позволяет просматривать общую статистику безопасности, графики соответствия, состояние активов и другие ключевые показатели.' 
  },
  { 
    id: 'access.assets', 
    name: 'Assets', 
    category: 'access', 
    description: 'Доступ к управлению активами. Позволяет просматривать список активов, их статус безопасности, уязвимости, проводить сканирование и управлять конфигурацией активов.' 
  },
  { 
    id: 'access.compliance', 
    name: 'Compliance', 
    category: 'access', 
    description: 'Доступ к разделу соответствия стандартам. Позволяет просматривать графики соответствия, заполнять анкеты по стандартам (DORA, GDPR, ISO 27001, NIS2, PCI DSS, SOC2, SWIFT CSCF), анализировать результаты соответствия.' 
  },
  { 
    id: 'access.customerTrust', 
    name: 'Security Governance & Documentation', 
    category: 'access', 
    description: 'Доступ к разделу управления безопасностью и документации. Позволяет управлять политиками безопасности, репозиторием доказательств, внутренними аудитами, реестром рисков, обучением и календарем управления.' 
  },
  { 
    id: 'access.suppliers', 
    name: 'Suppliers', 
    category: 'access', 
    description: 'Доступ к управлению поставщиками. Позволяет просматривать список поставщиков, их уровень соответствия требованиям безопасности, проводить оценки поставщиков и управлять отношениями с поставщиками.' 
  },
  { 
    id: 'access.reports', 
    name: 'Reports', 
    category: 'access', 
    description: 'Доступ к отчетам. Позволяет просматривать отчеты о сканировании, анализировать уязвимости, экспортировать отчеты в различных форматах (PDF, Excel) и управлять историей отчетов.' 
  },
  { 
    id: 'access.integrations', 
    name: 'Integrations', 
    category: 'access', 
    description: 'Доступ к управлению интеграциями. Позволяет настраивать интеграции с внешними системами (сканеры уязвимостей, SIEM, системы мониторинга), управлять подключениями и синхронизацией данных.' 
  },
  { 
    id: 'access.notifications', 
    name: 'Notifications', 
    category: 'access', 
    description: 'Доступ к уведомлениям. Позволяет просматривать системные уведомления, настраивать параметры уведомлений (email, push, системные), управлять предупреждениями о безопасности и важных событиях.' 
  },
  { 
    id: 'access.admin', 
    name: 'Admin Access', 
    category: 'access', 
    description: 'Доступ к административной панели. Позволяет просматривать административную панель управления, но не дает права на изменение настроек системы и управление пользователями (требуются дополнительные разрешения manage.users, manage.roles).' 
  },
  { 
    id: 'manage.users', 
    name: 'Manage Users', 
    category: 'manage', 
    description: 'Управление пользователями. Позволяет создавать, редактировать и удалять пользователей, назначать роли и разрешения, управлять профилями пользователей и настройками доступа.' 
  },
  { 
    id: 'manage.roles', 
    name: 'Manage Roles', 
    category: 'manage', 
    description: 'Управление ролями. Позволяет создавать, редактировать и удалять роли пользователей, настраивать наборы разрешений для ролей, управлять иерархией ролей и правами доступа.' 
  },
  { 
    id: 'system.config', 
    name: 'System Config', 
    category: 'system', 
    description: 'Конфигурация системы. Позволяет изменять настройки системы, параметры безопасности, конфигурацию сканеров, настройки интеграций и другие системные параметры.' 
  },
  { 
    id: 'system.logs', 
    name: 'System Logs', 
    category: 'system', 
    description: 'Доступ к системным логам. Позволяет просматривать системные логи, логи аудита, логи безопасности, анализировать события системы и отслеживать активность пользователей.' 
  },
  { 
    id: 'security.incidents', 
    name: 'Security Incidents', 
    category: 'security', 
    description: 'Управление инцидентами безопасности. Позволяет создавать, редактировать и закрывать инциденты безопасности, управлять жизненным циклом инцидентов, назначать ответственных и отслеживать статус инцидентов.' 
  },
  { 
    id: 'security.alerts', 
    name: 'Security Alerts', 
    category: 'security', 
    description: 'Управление оповещениями безопасности. Позволяет просматривать и управлять оповещениями о безопасности, настраивать правила оповещений, управлять приоритетами оповещений и реагировать на угрозы.' 
  },
]

export default function AdminPanel() {
  const { t } = useI18n()
  const { user: currentUser, token, refreshUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [addError, setAddError] = useState<string>('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
    const createEmptyForm = (defaultOrganization = ''): UserFormData => ({
      username: '',
      email: '',
      password: '',
      role: 'user',
      permissions: ['access.dashboard'],
      organization: defaultOrganization,
      organizations: [],
      fullName: '',
      phone: '',
      position: ''
    })

    const [formData, setFormData] = useState<UserFormData>(createEmptyForm())
  
  const [orgs, setOrgs] = useState<OrgRecord[]>([])
  const [orgFilter, setOrgFilter] = useState('')
    const [orgNames, setOrgNames] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<'users' | 'permissions'>('users')

  const roleNorm = String(currentUser?.role || '').toLowerCase()
  const permList = Array.isArray(currentUser?.permissions) ? currentUser.permissions : []
  // Platform admin only — case-insensitive role + legacy admin|all permissions (not org OWNER)
  const isAdmin =
    roleNorm === 'admin' || permList.includes('admin') || permList.includes('all')

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_ENDPOINTS.ADMIN_USERS}?all=1`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(t('admin.error.fetchUsers'))
      }

      const data = await response.json()
      const normalized = (data.users || []).map((u: any) => ({
        ...u,
        permissions: Array.isArray(u.permissions) ? u.permissions : []
      }))
      setUsers(normalized)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.error.generic'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    void fetchUsers()
    void fetchOrganizations()
    void fetchOrgNames()
  }, [isAdmin])
  const fetchOrganizations = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_ORGANIZATIONS, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        const rows = Array.isArray(data) ? data : (data?.organizations || [])
        const records: OrgRecord[] = rows
          .filter((o: { id?: string; name?: string }) => o?.id && o?.name)
          .map((o: { id: string; name: string; userCount?: number; assetCount?: number }) => ({
            id: String(o.id),
            name: String(o.name),
            userCount: o.userCount,
            assetCount: o.assetCount,
            deletionStatus: (o as any).deletionStatus,
            deletionScheduledFor: (o as any).deletionScheduledFor ?? null,
            deletionComment: (o as any).deletionComment ?? null,
          }))
        setOrgs(records)
        setOrgNames(records.map((o) => o.name))
      }
    } catch {}
  }

    const fetchOrgNames = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_ORG_NAMES, { headers: { 'Authorization': `Bearer ${token}` } })
        if (res.ok) {
          const data = await res.json()
          const list = Array.isArray(data.organizations) ? data.organizations : []
          const normalized = Array.from(new Set(list.filter(Boolean).map((n: any) => String(n).trim()))).sort((a, b) => a.localeCompare(b))
          setOrgNames(normalized)
        }
      } catch {}
    }

    const orgRecords = useMemo(() => {
      const byId = new Map<string, OrgRecord>()
      for (const o of orgs) {
        if (o?.id && o?.name) byId.set(o.id, o)
      }
      return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [orgs])

    const activeOrgRecords = useMemo(
      () => orgRecords.filter((o) => o.deletionStatus !== 'PENDING_DELETION'),
      [orgRecords],
    )

    const normalizedOrgNames = useMemo(
      () => activeOrgRecords.map((o) => o.name),
      [activeOrgRecords],
    )

    const normalizedOrgIds = useMemo(() => activeOrgRecords.map((o) => o.id), [activeOrgRecords])

    const resolveOrgId = (ref: string) => {
      const trimmed = String(ref ?? '').trim()
      if (!trimmed) return ''
      if (UUID_RE.test(trimmed)) {
        return orgRecords.some((o) => o.id === trimmed) ? trimmed : ''
      }
      const byName = orgRecords.find((o) => o.name === trimmed)
      return byName?.id ?? ''
    }

    const organizationSelectOptions = useMemo(
      () => activeOrgRecords.map((org) => ({ value: org.id, label: org.name })),
      [activeOrgRecords],
    )

    const roleSelectOptions = useMemo(
      () => [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
      ],
      [],
    )

    useEffect(() => {
      if (!isAdmin || !showAddModal) return
        void fetchOrgNames()
        setFormData((prev) => {
          const defaultOrg =
            prev.organization && normalizedOrgIds.includes(prev.organization)
              ? prev.organization
              : (normalizedOrgIds[0] || '')
          const filteredExtras = prev.organizations.filter(
            (org) => normalizedOrgIds.includes(org) && org !== defaultOrg,
          )
          return { ...prev, organization: defaultOrg, organizations: filteredExtras }
        })
    }, [isAdmin, showAddModal, normalizedOrgIds])

    const closeAddUserModal = () => {
      setShowAddModal(false)
      setAddError('')
      setFormData(createEmptyForm(normalizedOrgIds[0] || ''))
    }

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setAddError('')
        const orgsInput = formData.organizations
        const primaryOrg = formData.organization.trim()
        if (!primaryOrg || !normalizedOrgIds.includes(primaryOrg)) {
          setAddError('Please select a valid organization before creating a user')
          return
        }
        const additional = (orgsInput || []).filter(
          (org) => normalizedOrgIds.includes(org) && org !== primaryOrg,
        )
        const orgs = Array.from(new Set([primaryOrg, ...additional]))

      if (!formData.username.trim()) {
        setAddError('Username is required')
        return
      }
      if (!formData.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
        setAddError('Valid email is required')
        return
      }
      if (!formData.password || formData.password.length < 6) {
        setAddError('Password must be at least 6 characters')
        return
      }
        if (normalizedOrgIds.length === 0) {
          setAddError('No organizations available. Please register an organization first.')
          return
        }

      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          fullName: formData.fullName || undefined,
          phone: formData.phone || undefined,
          position: formData.position || undefined,
          permissions: formData.permissions,
          organizations: orgs,
        })
      })

      if (!response.ok) {
        let message = t('admin.error.createUser')
        try {
          const ct = response.headers.get('content-type') || ''
          if (ct.includes('application/json')) {
            const errorData = await response.json()
            message = errorData.message || errorData.error || message
          } else {
            message = await response.text() || message
          }
        } catch {}
        throw new Error(message)
      }

        setShowAddModal(false)
        setFormData(createEmptyForm(normalizedOrgIds[0] || ''))
      fetchUsers()
      await refreshUser()
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('admin.error.generic'))
    }
  }

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    try {
      // Build organizations payload: primary + additional
        const orgsInput = formData.organizations
        const primaryOrg = formData.organization.trim()
        if (!primaryOrg || !normalizedOrgIds.includes(primaryOrg)) {
          setError('Please select a valid organization before saving the user')
          return
        }
        const additional = (orgsInput || []).filter(
          (orgId) => normalizedOrgIds.includes(orgId) && orgId !== primaryOrg,
        )
        const orgs = Array.from(new Set([primaryOrg, ...additional])).filter(Boolean)

      const response = await fetch(API_ENDPOINTS.ADMIN_USER((editingUser as any).id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          fullName: formData.fullName || undefined,
          phone: formData.phone || undefined,
          position: formData.position || undefined,
          role: formData.role,
          permissions: formData.permissions,
          organizations: orgs,
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || errorData.error || t('admin.error.updateUser'))
      }

        setShowEditModal(false)
        setEditingUser(null)
        setFormData(createEmptyForm(normalizedOrgIds[0] || ''))
      fetchUsers()
      await refreshUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.error.generic'))
    }
  }

  const handleDeleteUser = async (id: string) => {
    if (!confirm(t('admin.confirmDelete'))) return

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete user')
      }

      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.error.generic'))
    }
  }

  const openEditModal = async (user: User) => {
      setEditingUser(user)
      setShowEditModal(true)
      let detail: User = user
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_USER(user.id), {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
        if (res.ok) {
          detail = { ...user, ...(await res.json()) }
        }
      } catch {
        /* use list row */
      }

      const orgRefs = detail.organizations || []
      const orgIds = orgRefs
        .map((o) =>
          typeof o === 'object' && o && 'id' in o
            ? String((o as { id: string }).id)
            : resolveOrgId(String(o)),
        )
        .filter(Boolean)
      const primary = orgIds[0] || normalizedOrgIds[0] || ''
      const extras = orgIds.filter((id, idx) => idx > 0 && normalizedOrgIds.includes(id))

      setFormData({
        username: detail.username,
        email: detail.email,
        password: '',
        role: detail.role,
        permissions: Array.isArray(detail.permissions) ? detail.permissions : [],
        organization: primary,
        organizations: extras,
        fullName: detail.fullName || '',
        phone: detail.phone || '',
        position: detail.position || '',
      })
    }

  const togglePermission = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }))
  }

  // Handle permission change from PermissionsManagement component
  const handlePermissionChange = async (userId: string, permission: string, granted: boolean) => {
    try {
      const user = users.find(u => u.id === userId)
      if (!user) return

      const updatedPermissions = granted
        ? [...(user.permissions || []), permission]
        : (user.permissions || []).filter(p => p !== permission)

      const response = await fetch(API_ENDPOINTS.ADMIN_USER(userId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user.email,
          role: user.role,
          permissions: updatedPermissions,
          organizations: (user.organizations || [])
            .map((o) =>
              typeof o === 'object' && o && 'id' in o
                ? String((o as { id: string }).id)
                : resolveOrgId(String(o)),
            )
            .filter(Boolean),
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update permissions')
      }

      fetchUsers()
      await refreshUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permissions')
    }
  }

  if (!isAdmin) {
    return (
      <PageLayout pageScope="admin-panel-page" title={t('admin.title')}>
        <div className={adminStyles.accessDenied}>
          <div className={adminStyles.accessInner}>
            <AlertTriangle className={adminStyles.accessIcon} aria-hidden />
            <h2 className={adminStyles.accessTitle}>Access Denied</h2>
            <p className={adminStyles.accessText}>You need admin privileges to access this page.</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (loading) {
    return (
      <PageLayout pageScope="admin-panel-page" title={t('admin.title')}>
        <div className={adminStyles.loadingWrap}>
          <div className={adminStyles.loadingInner}>
            <div className={adminStyles.spinner} aria-hidden />
            <p className={adminStyles.loadingText}>{t('common.loading')}</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="admin-panel-page" title={t('admin.title')}
      actions={
        <div className={adminStyles.headerActions}>
          <AdminNavTabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as 'users' | 'permissions')}
            items={[
              { key: 'users', label: t('admin.users'), icon: <Users className={adminStyles.iconSm} aria-hidden /> },
              {
                key: 'permissions',
                label: t('admin.permissions'),
                icon: <KeyRound className={adminStyles.iconSm} aria-hidden />,
              },
            ]}
          />
          {activeTab === 'users' && (
            <Button
              type="button"
              size="small"
              color="primary"
              icon={<UserPlus className={adminStyles.iconSm} aria-hidden />}
              onClick={() => setShowAddModal(true)}
              disabled={normalizedOrgNames.length === 0}
              title={normalizedOrgNames.length === 0 ? 'Create an organization before adding users' : undefined}
            >
              {t('admin.addUser')}
            </Button>
          )}
        </div>
      }
    >
      <div className={listStyles.pageStack}>
      {error && (
        <div className={adminStyles.errorBanner}>
          <div className={adminStyles.errorRow}>
            <XCircle className={adminStyles.errorIcon} aria-hidden />
            <div>
              <h3 className={adminStyles.errorTitle}>Error</h3>
              <div className={adminStyles.errorBody}>{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Users tab */}
      {activeTab === 'permissions' && (
        <PermissionsManagement
          users={users}
          availablePermissions={availablePermissionsWithCategories}
          onPermissionChange={handlePermissionChange}
        />
      )}

      {/* Users tab */}
      {activeTab === 'users' && (
        <>
      <GovernanceListingSection
        title={t('admin.userManagement')}
        description="View accounts, edit users, and reset passwords."
      >
          <div className={listStyles.tableWrap}>
            <table className={listStyles.table}>
              <thead>
                <tr>
                  <th className={listStyles.th}>User</th>
                  <th className={listStyles.th}>Role</th>
                  <th className={listStyles.th}>Permissions</th>
                  <th className={listStyles.th}>Last Login</th>
                  <th className={listStyles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <TableEmptyRow colSpan={5}>
                    {t('admin.noUsersInTable', 'No users to display')}
                  </TableEmptyRow>
                ) : (
                  users.map((user) => (
                  <tr key={user.id} className={listStyles.row}>
                    <td className={listStyles.td}>
                      <div className={adminStyles.userCell}>
                        <div className={adminStyles.avatar}>
                          <span className={adminStyles.avatarLetter}>{user.username.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className={adminStyles.userMeta}>
                          <div className={adminStyles.userName}>{user.username}</div>
                          <div className={adminStyles.userEmail}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className={listStyles.td}>
                      <span className={`${adminStyles.rolePill} ${String(user.role || '').toLowerCase() === 'admin' ? adminStyles.roleAdmin : adminStyles.roleUser}`}>
                        {String(user.role || '').toLowerCase() === 'admin' ? t('admin.admin') : t('admin.user')}
                      </span>
                    </td>
                    <td className={listStyles.td}>
                      <div className={listStyles.cellStrong}>
                        {(Array.isArray(user.permissions) ? user.permissions.length : 0)} permissions
                      </div>
                    </td>
                    <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                      {new Date(user.lastLogin).toLocaleDateString()}
                    </td>
                    <td className={listStyles.td}>
                      <div className={adminStyles.actionGroup}>
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Edit className={adminStyles.iconSm} aria-hidden />}
                          onClick={() => void openEditModal(user)}
                          className={adminStyles.btnIcon}
                          aria-label="Edit user"
                        />
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<KeyRound className={adminStyles.iconSm} aria-hidden />}
                          onClick={async () => {
                            const newPassword = prompt(`Set new password for ${user.username}`)
                            if (!newPassword) return
                            const res = await fetch(API_ENDPOINTS.ADMIN_USER_PASSWORD((user as any).id), {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ newPassword })
                            })
                            if (!res.ok) {
                              const err = await res.json().catch(()=>({message:'Failed'}))
                              alert(err.message || 'Failed to update password')
                            } else {
                              alert('Password updated')
                            }
                          }}
                          className={adminStyles.btnIconKey}
                          title="Reset Password"
                          aria-label="Reset password"
                        />
                        {(user as any).id !== (currentUser as any)?.id && (
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<Trash2 className={adminStyles.iconSm} aria-hidden />}
                            onClick={() => handleDeleteUser((user as any).id)}
                            className={adminStyles.btnIconDanger}
                            aria-label="Delete user"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </GovernanceListingSection>
        </>
      )}

      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className={adminStyles.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="admin-add-user-title">
          <div className={adminStyles.modalPanel}>
            <div className={adminStyles.modalBody}>
              <h3 id="admin-add-user-title" className={adminStyles.modalTitle}>
                {t('admin.addUser')}
              </h3>
              {normalizedOrgNames.length === 0 ? (
                <div className={adminStyles.alertWarn}>
                  No organizations are registered yet. Please create an organization before adding users.
                </div>
              ) : (
                <form onSubmit={handleAddUser} className={adminStyles.formStack}>
                  {addError && <div className={adminStyles.alertDanger}>{addError}</div>}
                  <TextField
                    label="Username"
                    autoComplete="username"
                    value={formData.username}
                    onChange={(v) => setFormData((prev) => ({ ...prev, username: v }))}
                    minWidth="100%"
                  />
                  <TextField
                    label="Full Name"
                    autoComplete="name"
                    value={formData.fullName}
                    onChange={(v) => setFormData((prev) => ({ ...prev, fullName: v }))}
                    minWidth="100%"
                  />
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(v) => setFormData((prev) => ({ ...prev, email: v }))}
                    minWidth="100%"
                  />
                  <TextField
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={(v) => setFormData((prev) => ({ ...prev, password: v }))}
                    minWidth="100%"
                  />
                  <Select<string>
                    label="Organization"
                    minWidth="100%"
                    placeholder="Select organization"
                    options={organizationSelectOptions}
                    value={formData.organization || null}
                    onChange={(v) => setFormData((prev) => ({ ...prev, organization: v ?? '' }))}
                  />
                  <TextField
                    label="Position / role"
                    value={formData.position}
                    onChange={(v) => setFormData((prev) => ({ ...prev, position: v }))}
                    minWidth="100%"
                  />
                  <TextField
                    label="Phone"
                    type="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={(v) => setFormData((prev) => ({ ...prev, phone: v }))}
                    minWidth="100%"
                  />
                  <Select<string>
                    label="Role"
                    minWidth="100%"
                    options={roleSelectOptions}
                    value={formData.role}
                    onChange={(v) => v != null && setFormData((prev) => ({ ...prev, role: v }))}
                  />
                  <div>
                    <span className={listStyles.fieldLabel}>Permissions</span>
                    <div className={adminStyles.permissionList}>
                      {availablePermissionsWithCategories.map((permission) => (
                        <label key={permission.id} className={adminStyles.permissionRow}>
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.id)}
                            onChange={() => togglePermission(permission.id)}
                          />
                          <div className={adminStyles.permissionTextWrap}>
                            <div className={adminStyles.permissionName}>{permission.name}</div>
                            <div className={adminStyles.permissionDesc}>{permission.description}</div>
                            <div className={adminStyles.permissionId}>{permission.id}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className={listStyles.fieldLabel}>Additional organizations</span>
                    <select
                      multiple
                      value={formData.organizations}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map((option) => option.value)
                        setFormData((prev) => ({ ...prev, organizations: selected }))
                      }}
                      className={adminStyles.multiselect}
                    >
                      {orgRecords
                        .filter((org) => org.id !== formData.organization)
                        .map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                    </select>
                    <p className={adminStyles.fieldHint}>Hold Ctrl/Cmd to select multiple entries.</p>
                  </div>
                  <div className={adminStyles.modalFooter}>
                    <Button type="button" size="small" color="secondary" onClick={closeAddUserModal}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" size="small" color="primary">
                      {t('common.save')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className={adminStyles.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="admin-edit-user-title">
          <div className={adminStyles.modalPanel}>
            <div className={adminStyles.modalBody}>
              <h3 id="admin-edit-user-title" className={adminStyles.modalTitle}>
                {t('admin.editUser')}
              </h3>
              <form onSubmit={handleEditUser} className={adminStyles.formStack} id="admin-edit-user-form">
                <TextField
                  label="Username"
                  value={formData.username}
                  onChange={() => {}}
                  readOnly
                  minWidth="100%"
                />
                <TextField
                  label="Full Name"
                  autoComplete="name"
                  value={formData.fullName}
                  onChange={(v) => setFormData((prev) => ({ ...prev, fullName: v }))}
                  minWidth="100%"
                />
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(v) => setFormData((prev) => ({ ...prev, email: v }))}
                  minWidth="100%"
                />
                <Select<string>
                  label="Organization"
                  minWidth="100%"
                  placeholder="Select organization"
                  options={organizationSelectOptions}
                  value={formData.organization || null}
                  onChange={(v) => setFormData((prev) => ({ ...prev, organization: v ?? '' }))}
                />
                <TextField
                  label="Position / role"
                  value={formData.position}
                  onChange={(v) => setFormData((prev) => ({ ...prev, position: v }))}
                  minWidth="100%"
                />
                <TextField
                  label="Phone"
                  type="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={(v) => setFormData((prev) => ({ ...prev, phone: v }))}
                  minWidth="100%"
                />
                <Select<string>
                  label="Role"
                  minWidth="100%"
                  options={roleSelectOptions}
                  value={formData.role}
                  onChange={(v) => v != null && setFormData((prev) => ({ ...prev, role: v }))}
                />
                <div>
                  <span className={listStyles.fieldLabel}>Permissions</span>
                  <div className={adminStyles.permissionList}>
                    {availablePermissionsWithCategories.map((permission) => (
                      <label key={permission.id} className={adminStyles.permissionRow}>
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission.id)}
                          onChange={() => togglePermission(permission.id)}
                        />
                        <div className={adminStyles.permissionTextWrap}>
                          <div className={adminStyles.permissionName}>{permission.name}</div>
                          <div className={adminStyles.permissionDesc}>{permission.description}</div>
                          <div className={adminStyles.permissionId}>{permission.id}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <span className={listStyles.fieldLabel}>Additional organizations</span>
                  <select
                    multiple
                    value={formData.organizations}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map((option) => option.value)
                      setFormData((prev) => ({ ...prev, organizations: selected }))
                    }}
                    className={adminStyles.multiselect}
                  >
                    {orgRecords
                      .filter((org) => org.id !== formData.organization)
                      .map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                  </select>
                  <p className={adminStyles.fieldHint}>Hold Ctrl/Cmd to select multiple entries.</p>
                </div>
                                {formData.organization ? (
                  <UserStandardAccessPanel
                    userId={editingUser.id}
                    organizationId={formData.organization}
                  />
                ) : null}
<div className={adminStyles.modalFooter}>
                  <Button
                    type="button"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingUser(null)
                      setFormData(createEmptyForm(normalizedOrgIds[0] || ''))
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="small" color="primary">
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </PageLayout>
  )
}